class CartDrawerAddons extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.cart-drawer-addons__track');
    this.prevBtn = this.querySelector('.cart-drawer-addons__arrow--prev');
    this.nextBtn = this.querySelector('.cart-drawer-addons__arrow--next');
    this.limit = parseInt(this.dataset.limit, 10) || 4;
    this.intent = this.dataset.intent || 'complementary';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.isDown = false;
    this.moved = false;
    this.startX = 0;
    this.scrollStart = 0;

    this.prevBtn.addEventListener('click', () => this.scrollByCard(-1));
    this.nextBtn.addEventListener('click', () => this.scrollByCard(1));
    this.track.addEventListener('scroll', this.updateArrows.bind(this));
    window.addEventListener('resize', this.updateArrows.bind(this));
    this.bindDrag();
  }

  connectedCallback() {
    this.refresh();
  }

  async refresh() {
    try {
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      const cart = await cartResponse.json();

      if (!cart.items.length) {
        this.classList.add('is-empty');
        return;
      }

      const excludeIds = cart.items.map((item) => item.product_id);
      const seedProductId = cart.items[cart.items.length - 1].product_id;
      const recResponse = await fetch(
        `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${seedProductId}&limit=${this.limit + excludeIds.length}&intent=${this.intent}`
      );
      const recData = await recResponse.json();
      const products = (recData.products || [])
        .filter((product) => !excludeIds.includes(product.id))
        .slice(0, this.limit);

      if (!products.length) {
        this.classList.add('is-empty');
        return;
      }

      this.classList.remove('is-empty');
      this.renderCards(products);
    } catch (error) {
      this.classList.add('is-empty');
    }
  }

  renderCards(products) {
    this.track.innerHTML = products.map((product) => this.cardMarkup(product)).join('');
    this.track.querySelectorAll('[data-cda-add]').forEach((button) => {
      button.addEventListener('click', this.handleAdd.bind(this));
    });
    this.track.scrollTo({ left: 0 });
    this.updateArrows();
  }

  cardMarkup(product) {
    const variant = product.variants.find((item) => item.available) || product.variants[0];
    const image = product.featured_image || (product.images && product.images[0]) || '';
    const price = this.formatMoney(variant ? variant.price : product.price);
    const available = variant && variant.available;

    return `
      <li class="cart-drawer-addons__card">
        <a href="${product.url}" class="cart-drawer-addons__card-media">
          <img src="${image}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="150" height="150">
        </a>
        <div class="cart-drawer-addons__card-content">
          <a href="${product.url}" class="cart-drawer-addons__card-title">${this.escapeHtml(product.title)}</a>
          <span class="cart-drawer-addons__card-price">${price}</span>
          <button
            type="button"
            class="cart-drawer-addons__card-btn"
            data-cda-add
            data-variant-id="${variant ? variant.id : ''}"
            ${available ? '' : 'disabled'}
          >${available ? 'Add' : 'Sold out'}</button>
        </div>
      </li>
    `;
  }

  async handleAdd(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    if (!variantId || button.classList.contains('is-loading')) return;

    button.classList.add('is-loading');
    button.disabled = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      if (!response.ok) throw new Error('add-failed');

      await this.updateDrawer();

      button.classList.remove('is-loading');
      button.classList.add('is-added');
      button.textContent = 'Added';

      this.refresh();
    } catch (error) {
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  }

  async updateDrawer() {
    const response = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
    const sections = await response.json();
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents({ id: null, sections });
    }
  }

  scrollByCard(direction) {
    const card = this.track.querySelector('.cart-drawer-addons__card');
    if (!card) return;
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap || 0);
    const amount = card.getBoundingClientRect().width + gap;
    const isRtl = getComputedStyle(this).direction === 'rtl';
    const scrollDirection = isRtl ? -direction : direction;
    this.track.scrollBy({ left: amount * scrollDirection, behavior: 'smooth' });
  }

  updateArrows() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const currentScroll = Math.abs(this.track.scrollLeft);
    this.prevBtn.disabled = currentScroll <= 5;
    this.nextBtn.disabled = currentScroll >= maxScroll - 5;
  }

  bindDrag() {
    this.track.addEventListener('mousedown', (event) => {
      this.isDown = true;
      this.moved = false;
      this.track.classList.add('is-dragging');
      this.startX = event.pageX;
      this.scrollStart = this.track.scrollLeft;
    });

    window.addEventListener('mouseup', () => {
      this.isDown = false;
      this.track.classList.remove('is-dragging');
    });

    window.addEventListener('mousemove', (event) => {
      if (!this.isDown) return;
      event.preventDefault();
      const delta = event.pageX - this.startX;
      if (Math.abs(delta) > 3) this.moved = true;
      this.track.scrollLeft = this.scrollStart - delta;
    });

    this.track.addEventListener(
      'click',
      (event) => {
        if (this.moved) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  }

  formatMoney(cents) {
    const format = this.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountWithComma = amount.replace(/\B(?=(\d{3})+(?!\d)\.)/g, ',');

    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','));
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}

customElements.define('cart-drawer-addons', CartDrawerAddons);
